import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useUserStore } from '@/stores/user'
import { useEntityStore } from '@/stores/entity'
import { Roles } from '@/types/common'

/**
 * Resolves the default timeFrameId and entityId for the current user.
 * Priority: localStorage (last viewed) → server defaults (current TF + first entity).
 * Returns null if no valid defaults can be resolved.
 */
function resolveDefaults(): { timeFrameId: number; entityId: number } | null {
  const userStore = useUserStore()
  const entityStore = useEntityStore()

  // Collect valid child entity IDs (type 2 = Child, i.e. fillable entities)
  const ENTITY_TYPE_CHILD = 2
  const validEntityIds = new Set<number>()
  for (const e of userStore.entities) {
    if (e.type === ENTITY_TYPE_CHILD) {
      validEntityIds.add(e.entityId)
    }
  }

  // Try localStorage values first
  let tfId = entityStore.selectedTimeFrameId
  let entId = entityStore.selectedEntityId

  // Validate that localStorage entity is still in the user's permitted set
  if (entId && !validEntityIds.has(entId)) {
    entId = null
  }

  // Fall back to server defaults
  if (!tfId) {
    tfId = userStore.defaultTimeFrameId
  }
  if (!entId && validEntityIds.size > 0) {
    entId = validEntityIds.values().next().value!
  }

  if (tfId && entId) {
    return { timeFrameId: tfId, entityId: entId }
  }
  return null
}

/** beforeEnter guard for routes that need default timeFrameId/entityId */
function redirectWithDefaults(basePath: string) {
  return () => {
    const defaults = resolveDefaults()
    if (defaults) {
      return `${basePath}/${defaults.timeFrameId}/${defaults.entityId}`
    }
    return { name: 'access-denied' }
  }
}

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/auth',
      name: 'auth-callback',
      component: () => import('@/views/AuthCallbackView.vue'),
      meta: { public: true },
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
      meta: { public: true },
    },
    {
      path: '/access-denied',
      name: 'access-denied',
      component: () => import('@/views/AccessDeniedView.vue'),
      meta: { public: true },
    },
    {
      path: '/',
      redirect: '/home',
    },

    // Home
    {
      path: '/home',
      name: 'home-redirect',
      beforeEnter: redirectWithDefaults('/home'),
      component: () => import('@/views/HomeView.vue'),
    },
    {
      path: '/home/:timeFrameId/:entityId',
      name: 'home',
      component: () => import('@/views/HomeView.vue'),
    },

    // Forms (paramless routes redirect to defaults)
    {
      path: '/forms/environment',
      beforeEnter: redirectWithDefaults('/forms/environment'),
      component: () => import('@/views/forms/FormEnvironmentView.vue'),
    },
    {
      path: '/forms/environment/:timeFrameId/:entityId',
      name: 'form-environment',
      component: () => import('@/views/forms/FormEnvironmentView.vue'),
    },
    {
      path: '/forms/ais',
      beforeEnter: redirectWithDefaults('/forms/ais'),
      component: () => import('@/views/forms/FormAisView.vue'),
    },
    {
      path: '/forms/ais/:timeFrameId/:entityId',
      name: 'form-ais',
      component: () => import('@/views/forms/FormAisView.vue'),
    },
    {
      path: '/forms/general',
      beforeEnter: redirectWithDefaults('/forms/general'),
      component: () => import('@/views/forms/FormGeneralView.vue'),
    },
    {
      path: '/forms/general/:timeFrameId/:entityId',
      name: 'form-general',
      component: () => import('@/views/forms/FormGeneralView.vue'),
    },
    {
      path: '/forms/terroir',
      beforeEnter: redirectWithDefaults('/forms/terroir'),
      component: () => import('@/views/forms/FormTerroirView.vue'),
    },
    {
      path: '/forms/terroir/:timeFrameId/:entityId',
      name: 'form-terroir',
      component: () => import('@/views/forms/FormTerroirView.vue'),
    },
    {
      path: '/forms/lobbying',
      beforeEnter: redirectWithDefaults('/forms/lobbying'),
      component: () => import('@/views/forms/FormLobbyingView.vue'),
    },
    {
      path: '/forms/lobbying/:timeFrameId/:entityId',
      name: 'form-lobbying',
      component: () => import('@/views/forms/FormLobbyingView.vue'),
    },

    // Management (role-gated)
    {
      path: '/manage/users',
      name: 'manage-users',
      component: () => import('@/views/manage/UsersView.vue'),
      meta: { requiresRole: Roles.Administrator },
    },
    {
      path: '/manage/entities',
      name: 'manage-entities',
      component: () => import('@/views/manage/EntitiesView.vue'),
      meta: { requiresRole: Roles.Administrator },
    },
    {
      path: '/manage/currencies',
      name: 'manage-currencies',
      component: () => import('@/views/manage/CurrenciesView.vue'),
      meta: { requiresRole: Roles.Administrator },
    },
    {
      path: '/manage/timeframes',
      name: 'manage-timeframes',
      component: () => import('@/views/manage/TimeFramesView.vue'),
      meta: { requiresRole: Roles.Administrator },
    },
    {
      path: '/manage/legal-entities',
      name: 'manage-legal-entities',
      component: () => import('@/views/manage/LegalEntitiesView.vue'),
      meta: { requiresRole: Roles.Administrator },
    },
    {
      path: '/manage/forms-config',
      name: 'manage-forms-config',
      component: () => import('@/views/manage/FormsConfigView.vue'),
      meta: { requiresRole: Roles.Administrator },
    },

    // Exports
    {
      path: '/exports',
      name: 'exports',
      component: () => import('@/views/ExportView.vue'),
    },

    // Error / Not Found
    {
      path: '/error',
      name: 'error',
      component: () => import('@/views/ErrorView.vue'),
      meta: { public: true },
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/views/NotFoundView.vue'),
      meta: { public: true },
    },
  ],
})

router.beforeEach(async (to) => {
  if (to.meta.public) return

  const authStore = useAuthStore()
  if (!authStore.isAuthenticated) {
    return { name: 'login' }
  }

  const userStore = useUserStore()
  if (!userStore.loaded) {
    try {
      await userStore.fetchUserInfo()
    } catch {
      return { name: 'login' }
    }
  }

  if (to.meta.requiresRole) {
    const requiredRole = to.meta.requiresRole as number
    if ((userStore.user?.roleId ?? 0) < requiredRole) {
      return { name: 'access-denied' }
    }
  }
})

// Page titles for screen readers and browser tabs (WCAG 2.4.2)
const pageTitles: Record<string, string> = {
  'home': 'Home',
  'home-redirect': 'Home',
  'form-environment': 'Making Form',
  'form-ais': 'Hosting Form',
  'form-general': 'People & Governance Form',
  'form-terroir': 'Terroir Form',
  'form-lobbying': 'Political Contribution Form',
  'manage-users': 'Manage Users',
  'manage-entities': 'Manage Entities',
  'manage-currencies': 'Manage Currencies',
  'manage-timeframes': 'Manage Fiscal Years',
  'manage-forms-config': 'Forms Configuration',
  'exports': 'Exports',
  'auth-callback': 'Signing in...',
  'login': 'Login',
  'access-denied': 'Access Denied',
  'error': 'Error',
  'not-found': 'Page Not Found',
}

// Sync route params → entity store so cross-form navigation remembers the selection
router.afterEach((to) => {
  // Update document title
  const name = typeof to.name === 'string' ? to.name : ''
  document.title = `${pageTitles[name] || 'Page'} — S&R Reporting`

  const tfId = Number(to.params.timeFrameId)
  const entId = Number(to.params.entityId)
  if (tfId && entId) {
    const entityStore = useEntityStore()
    entityStore.select(tfId, entId)
  }
})

export default router
